/**
 * URL -> what kind of page this is (pure, doctype-agnostic).
 *
 * Doctype pages come from the central route map (`getDoctypeRouteTable()` in
 * docRoutes.ts) plus the generic `/doc/*` and `/list/*` routes, so a new mapped
 * doctype is picked up without touching this file. Non-doctype pages (dashboards,
 * settings, LINE OA, embedded apps, ...) are listed in PAGE_PATTERNS below — the
 * route metadata lives here on purpose so erpRoutes.tsx doesn't grow.
 *
 * A test asserts that every path declared in erpRoutes.tsx / iframeAppRoutes.tsx
 * resolves to a known pattern, so a new route without metadata fails CI.
 */

import { matchPath } from "react-router-dom";
import { getDoctypeRouteTable } from "@/lib/config/docRoutes";
import { safeDecodeURIComponent } from "@/lib/utils";
import type { PageType, ResolvedPageRoute } from "./types";

interface PatternEntry {
  pattern: string;
  pageType: PageType;
  doctype?: string;
  /** Route param that identifies the record (doc name or non-doctype id) */
  nameParam?: string;
  /** Route param that carries the doctype (generic /doc and /list routes) */
  doctypeParam?: string;
}

/** Generic routes that work for any doctype. */
const GENERIC_DOCTYPE_PATTERNS: PatternEntry[] = [
  { pattern: "/list/:doctype", pageType: "list", doctypeParam: "doctype" },
  { pattern: "/doc/:doctype/new", pageType: "create", doctypeParam: "doctype" },
  { pattern: "/doc/:doctype/name/:name", pageType: "detail", doctypeParam: "doctype", nameParam: "name" },
  { pattern: "/doc/:doctype/:name", pageType: "detail", doctypeParam: "doctype", nameParam: "name" },
];

/** Doctype routes that exist in the router but not in routes.ts (aliases, legacy, forced-doctype pages). */
const EXTRA_DOCTYPE_PATTERNS: PatternEntry[] = [
  { pattern: "/item/:name", pageType: "detail", doctype: "Item", nameParam: "name" },
  { pattern: "/item-page2", pageType: "list", doctype: "Item" },
  { pattern: "/customer", pageType: "list", doctype: "Customer" },
  { pattern: "/orders/:name/legacy", pageType: "detail", doctype: "Sales Order", nameParam: "name" },
  { pattern: "/pos/profiles", pageType: "list", doctype: "POS Profile" },
  { pattern: "/pos/profiles/new", pageType: "create", doctype: "POS Profile" },
  { pattern: "/pos/profiles/:name", pageType: "detail", doctype: "POS Profile", nameParam: "name" },
];

/** Non-doctype pages under MainLayout (pages outside it have no AI rail). */
const PAGE_PATTERNS: PatternEntry[] = [
  { pattern: "/", pageType: "dashboard" },
  { pattern: "/menu", pageType: "tool" },
  { pattern: "/analytics/carts", pageType: "list" },
  { pattern: "/analytics/activity", pageType: "dashboard" },
  { pattern: "/analytics/social", pageType: "dashboard" },
  { pattern: "/analytics/realtime", pageType: "dashboard" },
  { pattern: "/marketing/meta", pageType: "dashboard" },
  { pattern: "/marketing/meta/campaign/:campaignId", pageType: "detail", nameParam: "campaignId" },
  { pattern: "/seo", pageType: "dashboard" },
  { pattern: "/seo/detail", pageType: "detail" },
  { pattern: "/dashboard/business", pageType: "dashboard" },
  { pattern: "/business", pageType: "dashboard" },
  { pattern: "/system-health", pageType: "dashboard" },
  { pattern: "/fulfillment", pageType: "dashboard" },
  { pattern: "/fulfillment/dashboard", pageType: "dashboard" },
  { pattern: "/fulfillment/awaiting-shipment", pageType: "list" },
  { pattern: "/fulfillment/orders", pageType: "list" },
  { pattern: "/fulfillment/sessions", pageType: "list" },
  { pattern: "/fulfillment/pack-and-ship", pageType: "tool" },
  { pattern: "/fulfillment/personalization", pageType: "list" },
  { pattern: "/fulfillment/settings", pageType: "settings" },
  { pattern: "/channels", pageType: "dashboard" },
  { pattern: "/channels/:channel", pageType: "detail", nameParam: "channel" },
  { pattern: "/channels/:channel/listings", pageType: "list" },
  { pattern: "/channels/:channel/product/new", pageType: "create" },
  { pattern: "/google-business", pageType: "settings" },
  { pattern: "/line-oa", pageType: "dashboard" },
  { pattern: "/line-oa/rich-menus", pageType: "list" },
  { pattern: "/line-oa/rich-menus/new", pageType: "editor" },
  { pattern: "/line-oa/broadcast", pageType: "list" },
  { pattern: "/line-oa/broadcast/new", pageType: "create" },
  { pattern: "/line-oa/audience", pageType: "list" },
  { pattern: "/line-oa/campaigns", pageType: "list" },
  { pattern: "/line-oa/campaigns/sent/:name", pageType: "detail", nameParam: "name" },
  { pattern: "/settings", pageType: "settings" },
  { pattern: "/settings/*", pageType: "settings" },
  { pattern: "/tools", pageType: "tool" },
  { pattern: "/storefront", pageType: "other" },
  { pattern: "/storefront/domains", pageType: "settings" },
  { pattern: "/storefront/settings", pageType: "settings" },
  { pattern: "/storefront/settings/*", pageType: "settings" },
  { pattern: "/storefront/payment-methods", pageType: "settings" },
  { pattern: "/storefront/payment-slips", pageType: "list" },
  { pattern: "/storefront/collections", pageType: "list" },
  { pattern: "/storefront/promotions", pageType: "list" },
  { pattern: "/storefront/promotions/new", pageType: "create" },
  { pattern: "/storefront/promotions/:name", pageType: "editor", nameParam: "name" },
  { pattern: "/storefront/mobile-design", pageType: "editor" },
  { pattern: "/promotions", pageType: "list" },
  { pattern: "/promotions/new", pageType: "create" },
  { pattern: "/promotions/:couponCode", pageType: "detail", nameParam: "couponCode" },
  { pattern: "/promotions/:couponCode/edit", pageType: "detail", nameParam: "couponCode" },
  { pattern: "/reviews", pageType: "list" },
  { pattern: "/rewards", pageType: "settings" },
  { pattern: "/support", pageType: "list" },
  { pattern: "/support/new", pageType: "create" },
  { pattern: "/support/:ticketNo", pageType: "detail", nameParam: "ticketNo" },
  { pattern: "/modules", pageType: "dashboard" },
  { pattern: "/module/:moduleSlug", pageType: "dashboard" },
  { pattern: "/grida-editor-test", pageType: "other" },
  { pattern: "/dev/*", pageType: "other" },
  // Embedded apps (persistent iframes) — the sub-path is in `path`
  { pattern: "/twenty-crm/*", pageType: "app" },
  { pattern: "/crm/*", pageType: "app" },
  { pattern: "/insights/*", pageType: "app" },
  { pattern: "/raven/*", pageType: "app" },
  { pattern: "/inbox/*", pageType: "app" },
  { pattern: "/blog/*", pageType: "app" },
  { pattern: "/ai", pageType: "app" },
  { pattern: "/sheets/*", pageType: "app" },
  { pattern: "/affine", pageType: "app" },
  { pattern: "/mail", pageType: "other" },
  { pattern: "/build", pageType: "tool" },
  { pattern: "/apps", pageType: "list" },
  { pattern: "/apps/:appName", pageType: "detail", nameParam: "appName" },
  { pattern: "/all-apps", pageType: "other" },
];

function mappedDoctypePatterns(): PatternEntry[] {
  return getDoctypeRouteTable().flatMap(({ doctype, list, new: newPath, detailPattern }) => [
    { pattern: list, pageType: "list" as const, doctype },
    { pattern: newPath, pageType: "create" as const, doctype },
    { pattern: detailPattern, pageType: "detail" as const, doctype, nameParam: "name" },
  ]);
}

let cachedPatterns: PatternEntry[] | null = null;

function allPatterns(): PatternEntry[] {
  if (!cachedPatterns) {
    cachedPatterns = [
      ...GENERIC_DOCTYPE_PATTERNS,
      ...mappedDoctypePatterns(),
      ...EXTRA_DOCTYPE_PATTERNS,
      ...PAGE_PATTERNS,
    ];
  }
  return cachedPatterns;
}

/** Most specific wins: static segment 3, param 2, splat 1 (same idea as react-router ranking). */
function specificity(pattern: string): number {
  return pattern
    .split("/")
    .filter(Boolean)
    .reduce((score, seg) => score + (seg === "*" ? 1 : seg.startsWith(":") ? 2 : 3), 0);
}

function normalizePathname(pathname: string): string {
  const path = pathname.split(/[?#]/)[0] || "/";
  return path.length > 1 ? path.replace(/\/+$/, "") || "/" : path;
}

function decodeParam(value: string | undefined): string | undefined {
  if (value === undefined || value === "") return undefined;
  return safeDecodeURIComponent(value).trim() || undefined;
}

export function resolvePageRoute(pathname: string): ResolvedPageRoute {
  const path = normalizePathname(pathname);
  let best: { entry: PatternEntry; params: Record<string, string | undefined>; score: number } | null =
    null;

  for (const entry of allPatterns()) {
    const match = matchPath({ path: entry.pattern, end: true }, path);
    if (!match) continue;
    const score = specificity(entry.pattern);
    if (!best || score > best.score) best = { entry, params: match.params, score };
  }

  if (!best) return { pageType: "other" };

  const { entry, params } = best;
  const doctype = entry.doctypeParam ? decodeParam(params[entry.doctypeParam]) : entry.doctype;
  const name = entry.nameParam ? decodeParam(params[entry.nameParam]) : undefined;

  return {
    pageType: entry.pageType,
    pattern: entry.pattern,
    ...(doctype ? { doctype } : {}),
    ...(name ? { name } : {}),
    ...(name && !doctype && entry.nameParam ? { recordRef: entry.nameParam } : {}),
  };
}
